import React, { useEffect, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import {
  FiAlertCircle,
  FiCalendar,
  FiCheckCircle,
  FiGlobe,
  FiMail,
  FiPhone,
  FiSearch,
  FiTag,
  FiUser,
  FiUsers,
} from 'react-icons/fi';

import TextField from '../auth/TextField';
import { sendEnquiry } from '../../api/client';
import { useAuth } from '../../context/AuthContext';

const TITLE_ID = 'mm-enquiry-title';

const EMPTY = {
  condition: '',
  name: '',
  email: '',
  phone: '',
  notes: '',
  consent: false,
};

/** Mirrors backend/enquiries.py. The server revalidates all of it. */
function validate(values) {
  const errors = {};
  if (!values.condition.trim()) {
    errors.condition = 'Tell us what you’d like help with.';
  }
  if (!values.name.trim()) errors.name = 'Please tell us your name.';
  else if (values.name.trim().length < 2) errors.name = 'That name looks too short.';

  if (!values.email.trim()) errors.email = 'Please enter your email address.';
  else if (!/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(values.email.trim()))
    errors.email = 'That doesn’t look like a valid email address.';

  if (values.phone.trim() && !/^[0-9+()\-.\s]{6,32}$/.test(values.phone.trim()))
    errors.phone = 'That doesn’t look like a phone number.';

  if (!values.consent)
    errors.consent = 'Please confirm we can contact you about this.';

  return errors;
}

function Summary({ availability, language, forWho }) {
  return (
    <ul className="mm-enquiry__summary">
      <li>
        <FiCalendar size={14} aria-hidden="true" />
        <span className="mm-enquiry__summary-label">Availability</span>
        {availability}
      </li>
      <li>
        <FiGlobe size={14} aria-hidden="true" />
        <span className="mm-enquiry__summary-label">Language</span>
        {language}
      </li>
      <li>
        <FiUsers size={14} aria-hidden="true" />
        <span className="mm-enquiry__summary-label">For</span>
        {forWho}
      </li>
    </ul>
  );
}

/**
 * Turns the hero search into a request a human answers.
 *
 * There is no roster to search yet, so a results page would be theatre. This
 * takes the query the person already built, asks for the two things needed to
 * reply to them, and posts it — the API records it and emails the match inbox.
 *
 * Opens pre-filled: the condition and both dropdowns carry over, and a
 * signed-in customer's name and email are filled in from their account.
 */
export default function EnquiryModal({ show, query, onHide }) {
  const { user } = useAuth();

  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState(null);
  const [result, setResult] = useState(null);

  const availability = query?.availability || 'Any time';
  const language = query?.language || 'Any language';
  const forWho = query?.forWho || 'Myself';

  // Re-seeded each time it opens rather than on mount: the person may search
  // again with different terms, and a stale condition in the box would be
  // worse than an empty one.
  useEffect(() => {
    if (!show) return;
    setValues({
      ...EMPTY,
      condition: query?.condition || '',
      // The quiz arrives with a note summarising its answers. A plain search
      // carries none, and the field stays empty.
      notes: query?.notes || '',
      name: user?.name || '',
      email: user?.email || '',
    });
    setErrors({});
    setFailure(null);
    setResult(null);
  }, [show, query, user]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setValues((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFailure(null);

    const clientErrors = validate(values);
    if (Object.keys(clientErrors).length) {
      setErrors(clientErrors);
      return;
    }

    setSubmitting(true);
    try {
      const response = await sendEnquiry({
        ...values,
        availability,
        language,
        forWho,
      });
      setResult(response);
    } catch (err) {
      // A 400 carries per-field reasons; 429 and anything else are about the
      // request as a whole, and the server's own wording is the clearest.
      if (err.body?.errors) {
        setErrors(err.body.errors);
        setFailure('Please check the highlighted fields.');
      } else {
        setFailure(
          err.status === 429
            ? err.message
            : 'Couldn’t send that just now. Please check your connection and try again.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    // Both branches carry the same id on their heading, so the dialog is
    // announced by whichever one is showing — a modal with no accessible name
    // is read out as just "dialog".
    <Modal
      show={show}
      onHide={onHide}
      centered
      scrollable
      className="mm-enquiry"
      aria-labelledby={TITLE_ID}
    >
      {result ? (
        <>
          <Modal.Header closeButton closeLabel="Close" />
          <Modal.Body className="mm-enquiry__done">
            <span className="mm-enquiry__done-icon" aria-hidden="true">
              <FiCheckCircle size={30} />
            </span>
            <h2 className="mm-enquiry__done-title" id={TITLE_ID}>
              {result.duplicate ? 'Already with us' : 'Request sent'}
            </h2>
            <p className="mm-enquiry__done-text">{result.message}</p>
            {result.reference && (
              <p className="mm-enquiry__ref">
                Reference <strong>{result.reference}</strong>
              </p>
            )}
            <Button className="mt-3 px-4" onClick={onHide}>
              Done
            </Button>
          </Modal.Body>
        </>
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          <Modal.Header closeButton closeLabel="Close">
            <div>
              <Modal.Title className="mm-enquiry__title" id={TITLE_ID}>
                Let&rsquo;s find your therapist
              </Modal.Title>
              <p className="mm-enquiry__sub">
                We&rsquo;re matching people by hand while we grow our clinician
                list, so tell us where to reach you and a real person will come
                back within one working day.
              </p>
            </div>
          </Modal.Header>

          <Modal.Body>
            <Summary
              availability={availability}
              language={language}
              forWho={forWho}
            />

            <TextField
              label="What would you like help with?"
              name="condition"
              icon={FiTag}
              value={values.condition}
              onChange={handleChange}
              error={errors.condition}
              placeholder="Anxiety, sleep, a relationship…"
              required
            />

            <TextField
              label="Your name"
              name="name"
              icon={FiUser}
              value={values.name}
              onChange={handleChange}
              error={errors.name}
              autoComplete="name"
              required
            />

            <TextField
              label="Email address"
              name="email"
              type="email"
              icon={FiMail}
              value={values.email}
              onChange={handleChange}
              error={errors.email}
              autoComplete="email"
              hint="This is where we'll send your matches."
              required
            />

            <TextField
              label="Phone"
              name="phone"
              type="tel"
              icon={FiPhone}
              value={values.phone}
              onChange={handleChange}
              error={errors.phone}
              autoComplete="tel"
            />

            <div className="mm-field">
              <label className="mm-field__label" htmlFor="mm-enquiry-notes">
                Anything else we should know?
                <span className="mm-faint fw-normal"> (optional)</span>
              </label>
              <textarea
                id="mm-enquiry-notes"
                name="notes"
                rows={3}
                className={`mm-field__control ${errors.notes ? 'mm-field__control--invalid' : ''}`}
                value={values.notes}
                onChange={handleChange}
                maxLength={1000}
                placeholder="Budget, language, whether you'd rather meet online or in person…"
              />
              {errors.notes && (
                <span className="mm-field__error" role="alert">
                  <FiAlertCircle size={13} aria-hidden="true" />
                  {errors.notes}
                </span>
              )}
            </div>

            <label className="mm-check mt-2">
              <input
                type="checkbox"
                name="consent"
                checked={values.consent}
                onChange={handleChange}
                aria-invalid={Boolean(errors.consent)}
              />
              <span>
                MindlyFriend can email or call me about this request. I understand
                this isn&rsquo;t an emergency service.
              </span>
            </label>
            {errors.consent && (
              <span className="mm-field__error d-block mt-1" role="alert">
                <FiAlertCircle size={13} aria-hidden="true" />
                {errors.consent}
              </span>
            )}

            {failure && (
              <p className="mm-enquiry__failure" role="alert">
                <FiAlertCircle size={15} aria-hidden="true" />
                {failure}
              </p>
            )}
          </Modal.Body>

          <Modal.Footer className="mm-enquiry__footer">
            <Button variant="link" className="mm-enquiry__cancel" onClick={onHide}>
              Cancel
            </Button>
            <Button type="submit" className="px-4" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner
                    animation="border"
                    size="sm"
                    className="me-2"
                    aria-hidden="true"
                  />
                  Sending…
                </>
              ) : (
                <>
                  <FiSearch size={16} className="me-2" aria-hidden="true" />
                  Send my request
                </>
              )}
            </Button>
          </Modal.Footer>
        </form>
      )}
    </Modal>
  );
}
